'use client';

import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'atoms/Button';
import { Link } from 'atoms/Link/Link';
import { Body, Title } from 'atoms/Typography/Typography';
import { BaseLoginForm } from 'molecules/BaseLoginForm';
import { Notification } from 'molecules/Notification';
import { gtmLoginModule } from 'modules/gtm/modules/sections/auth';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useRouter } from 'modules/next/hooks';
import { AppRoutes } from 'modules/shop/routes';
import { useUser } from 'modules/shop/user/hooks';
import {
  useValidationRules,
  useYupValidationResolver,
} from 'modules/validation/hooks';
import { getLoginValidationErrorMessages } from 'modules/validation/utils';
import { LoginCredentials } from 'commerce/scayle/auth/types';
import styles from './styles.module.css';

type LoginContentProps = {
  isPage?: boolean;
  redirectUrl?: string | null;
};

export const LoginContent: FC<LoginContentProps> = ({
  isPage,
  redirectUrl = AppRoutes.ACCOUNT,
}) => {
  const t = useTranslations();
  const [loginError, setLoginError] = useState<boolean>(false);
  const { push, refresh } = useRouter();
  const { login } = useUser();
  const { email, password } = useValidationRules();
  const resolver = useYupValidationResolver({
    email,
    password,
  });

  const { handleSubmit, control, reset } = useForm({
    resolver,
    mode: 'onChange',
  });

  const handleLogin = async (data: LoginCredentials) => {
    const user = await login(data);

    if (!!user) {
      setLoginError(false);

      if (redirectUrl) {
        push(redirectUrl);
      }
      /** ios needs to refresh page */
      refresh();
    } else {
      setLoginError(true);
      reset();
    }
  };

  const onSubmit = handleSubmit(handleLogin, (validationErrors) => {
    const errorMessage = getLoginValidationErrorMessages(validationErrors, t);
    gtmLoginModule.gtmPushFailedLogin(errorMessage);
  });

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.content}>
          <Title type="md" tag="h2">
            {t('login.popup.title')}
          </Title>
          <Body type="sm">{t('login.popup.subtitle')}</Body>
        </div>
        <BaseLoginForm control={control} onSubmit={onSubmit} />
      </div>
      <div className={styles.bottom}>
        <div className={styles.content}>
          <Title type="md" tag="h2">
            {t('login.popup.title-register')}
          </Title>
          <Body type="sm">
            {t.rich('login.popup.subtitle-register', {
              strong: (chunks) => <b>{chunks}</b>,
            })}
          </Body>
        </div>
        <Link href={AppRoutes.REGISTRATION}>
          <Button className={styles.buttonLink} type="submit">
            {t('login.buttons.register')}
          </Button>
        </Link>
      </div>
      <Notification
        variant={isPage ? 'base' : 'dark'}
        open={loginError}
        id="login-error"
        onClose={() => {
          setLoginError(false);
        }}
      >
        {t('login.error')}
      </Notification>
    </div>
  );
};
